import React from "react";
import { Box, Typography } from "@mui/material";

export default function DMCAPolicy() {
  const platformName = "Siki";
  const platformDomain = "siki.io";
  const companyName = "SIKI Holding Corp";
  const companyAddress = "8275 S Eastern Ave, Suite 200, Las Vegas, NV 89123";
  const dmcaEmail = "admin@siki.io";

  return (
    <Box sx={{ padding: "10px 40px" }}>
      <Typography className="fs-24 text-center my">
        DMCA Copyright Policy
      </Typography>
      <Typography className="fs-14 text-center my" style={{ fontStyle: 'italic' }}>
        Last Updated: January 1, 2025
      </Typography>

      {/* Introduction */}
      <div className="my-10">
        <Typography className="fs-16">Digital Millennium Copyright Act Policy</Typography>
        <Typography className="fs-14 text-justify my-10">
          {companyName} ("{platformName}") respects the intellectual property rights of others
          and expects its users to do the same. In accordance with the Digital Millennium
          Copyright Act of 1998 ("DMCA"), the text of which may be found on the U.S. Copyright
          Office website at{" "}
          <a href="http://www.copyright.gov/legislation/dmca.pdf" target="_blank" rel="noopener noreferrer">
            http://www.copyright.gov/legislation/dmca.pdf
          </a>, {platformName} will respond expeditiously to claims of copyright infringement
          committed using the {platformName} platform (accessible at https://{platformDomain})
          if such claims are reported to {platformName}'s Designated Copyright Agent identified below.
        </Typography>
        <Typography className="fs-14 text-justify my-10">
          If you are a copyright owner, or are authorized to act on behalf of one, or
          authorized to act under any exclusive right under copyright, please report alleged
          copyright infringements taking place on or through the {platformName} platform by
          completing the following DMCA Notice of Alleged Infringement and delivering it to
          {platformName}'s Designated Copyright Agent. Upon receipt of the Notice as described
          below, {platformName} will take whatever action, in its sole discretion, it deems
          appropriate, including removal of the challenged material from the platform.
        </Typography>
      </div>

      {/* DMCA Notice */}
      <div className="my-10">
        <Typography className="fs-16">DMCA Notice of Alleged Infringement ("Notice")</Typography>
        <Typography className="fs-14 text-justify my-10">
          To file a notice of infringement with us, you must provide a written communication
          (by email or regular mail) that sets forth the items specified below. Please note
          that you may be liable for damages (including costs and attorneys' fees) if you
          materially misrepresent that material is infringing your copyrights. Accordingly,
          if you are not sure whether material available online infringes your copyright,
          we suggest that you first contact an attorney.
        </Typography>
        <Typography className="fs-14 text-justify my-10">
          A proper DMCA Notice must include the following:
        </Typography>
        <ul>
          <li className="fs-14 text-white my-10">
            <strong>Identification of the copyrighted work:</strong> A physical or electronic
            signature of a person authorized to act on behalf of the owner of an exclusive
            right that is allegedly infringed.
          </li>
          <li className="fs-14 text-white my-10">
            <strong>Identification of the infringing material:</strong> Identification of the
            copyrighted work claimed to have been infringed, or, if multiple copyrighted works
            at a single online site are covered by a single notification, a representative list
            of such works at that site.
          </li>
          <li className="fs-14 text-white my-10">
            <strong>Location of infringing material:</strong> Identification of the material that
            is claimed to be infringing or to be the subject of infringing activity and that is
            to be removed or access to which is to be disabled, and information reasonably
            sufficient to permit {platformName} to locate the material. Please provide the specific
            URL(s) of the content on {platformName}.
          </li>
          <li className="fs-14 text-white my-10">
            <strong>Contact information:</strong> Information reasonably sufficient to permit
            {platformName} to contact you, such as an address, telephone number, and, if available,
            an email address.
          </li>
          <li className="fs-14 text-white my-10">
            <strong>Good faith statement:</strong> A statement that you have a good faith belief
            that use of the material in the manner complained of is not authorized by the copyright
            owner, its agent, or the law.
          </li>
          <li className="fs-14 text-white my-10">
            <strong>Accuracy statement:</strong> A statement that the information in the notification
            is accurate, and under penalty of perjury, that you are authorized to act on behalf of
            the owner of an exclusive right that is allegedly infringed.
          </li>
        </ul>
        <Typography className="fs-14 text-justify my-10">
          Please note that under Section 512(f) of the DMCA, any person who knowingly materially
          misrepresents that material or activity is infringing may be subject to liability.
        </Typography>
      </div>

      {/* Designated Copyright Agent */}
      <div className="my-10">
        <Typography className="fs-16">Designated Copyright Agent</Typography>
        <Typography className="fs-14 text-justify my-10">
          {platformName}'s Designated Copyright Agent to receive notifications of claimed infringement
          can be reached as follows:
        </Typography>
        <Box sx={{ padding: "20px", backgroundColor: "#1a1a1a", borderRadius: "8px", marginTop: "10px" }}>
          <Typography className="fs-14" style={{ marginBottom: "8px" }}>
            <strong>Attention: DMCA Copyright Agent</strong>
          </Typography>
          <Typography className="fs-14" style={{ marginBottom: "8px" }}>
            {companyName}
          </Typography>
          <Typography className="fs-14" style={{ marginBottom: "8px" }}>
            {companyAddress}
          </Typography>
          <Typography className="fs-14" style={{ marginBottom: "8px" }}>
            Email: <a href={`mailto:${dmcaEmail}`}>{dmcaEmail}</a>
          </Typography>
        </Box>
        <Typography className="fs-14 text-justify my-10">
          For clarity, only DMCA notices should be sent to the Designated Copyright Agent.
          Any other feedback, comments, requests for technical support, or other communications
          should be directed to {platformName} customer service through{" "}
          <a href="mailto:contact@siki.io">contact@siki.io</a>. You acknowledge that if you
          fail to comply with all of the requirements of this section, your DMCA notice may
          not be valid.
        </Typography>
      </div>

      {/* Counter-Notification */}
      <div className="my-10">
        <Typography className="fs-16">Counter-Notification Procedures</Typography>
        <Typography className="fs-14 text-justify my-10">
          If you believe that material you posted on the {platformName} platform was removed or
          access to it was disabled by mistake or misidentification, you may file a counter-notification
          with us (a "Counter-Notice") by submitting written notification to our Designated Copyright
          Agent. Pursuant to the DMCA, the Counter-Notice must include substantially the following:
        </Typography>
        <ul>
          <li className="fs-14 text-white my-10">
            Your physical or electronic signature.
          </li>
          <li className="fs-14 text-white my-10">
            Identification of the material that has been removed or to which access has been disabled
            and the location at which the material appeared before it was removed or access disabled
            (please provide the specific URL(s)).
          </li>
          <li className="fs-14 text-white my-10">
            A statement under penalty of perjury that you have a good faith belief that the material
            was removed or disabled as a result of mistake or misidentification of the material to
            be removed or disabled.
          </li>
          <li className="fs-14 text-white my-10">
            Your name, address, and telephone number, and a statement that you consent to the
            jurisdiction of the Federal District Court for the judicial district in which your
            address is located (or if you reside outside the United States, for any judicial
            district in which {platformName} may be found), and that you will accept service of
            process from the person who provided the original DMCA notification or an agent of
            such person.
          </li>
        </ul>
        <Typography className="fs-14 text-justify my-10">
          A party submitting a Counter-Notice should consult a legal advisor before filing a
          counter-notice. A counter-notice may result in the party who filed the original DMCA
          notice filing a lawsuit against you for copyright infringement. Please note that under
          Section 512(f) of the DMCA, any person who knowingly materially misrepresents that
          material or activity was removed or disabled by mistake or misidentification may be
          subject to liability.
        </Typography>
        <Typography className="fs-14 text-justify my-10">
          If a Counter-Notice is received by the Designated Copyright Agent, {platformName} may send
          a copy of the Counter-Notice to the original complaining party informing that person
          that {platformName} may replace the removed material or cease disabling it in 10 business
          days. Unless the copyright owner files an action seeking a court order against the
          content provider, member or user, the removed material may be replaced, or access to
          it restored, in 10 to 14 business days or more after receipt of the Counter-Notice,
          at {platformName}'s sole discretion.
        </Typography>
      </div>

      {/* Repeat Infringer Policy */}
      <div className="my-10">
        <Typography className="fs-16">Repeat Infringer Policy</Typography>
        <Typography className="fs-14 text-justify my-10">
          In accordance with the DMCA and other applicable law, {platformName} has adopted a policy
          of terminating, in appropriate circumstances and at {platformName}'s sole discretion, the
          accounts of users who are deemed to be repeat infringers. {platformName} may also, at its
          sole discretion, limit access to the {platformName} platform and/or terminate the accounts
          of any users who infringe any intellectual property rights of others, whether or not
          there is any repeat infringement.
        </Typography>
        <Typography className="fs-14 text-justify my-10">
          {platformName} will evaluate each case individually and may consider the following factors
          when determining whether a user is a repeat infringer:
        </Typography>
        <ul>
          <li className="fs-14 text-white my-10">
            The number of DMCA notices received regarding the user's content.
          </li>
          <li className="fs-14 text-white my-10">
            Whether the user has received prior warnings about copyright infringement.
          </li>
          <li className="fs-14 text-white my-10">
            The nature and severity of the infringement.
          </li>
          <li className="fs-14 text-white my-10">
            Whether the user has filed valid counter-notifications.
          </li>
        </ul>
      </div>

      {/* Modifications */}
      <div className="my-10">
        <Typography className="fs-16">Modifications to Policy</Typography>
        <Typography className="fs-14 text-justify my-10">
          {platformName} reserves the right to modify, alter, or add to this policy at any time,
          and users are encouraged to review this policy periodically. Any changes to this
          policy will be effective immediately upon posting to this page.
        </Typography>
      </div>

      {/* Contact */}
      <div className="my-10">
        <Typography className="fs-16">Contact Information</Typography>
        <Typography className="fs-14 text-justify my-10">
          For DMCA notices and counter-notices, please contact our Designated Copyright Agent
          at the contact information provided above.
        </Typography>
        <Typography className="fs-14 text-justify my-10">
          For all other inquiries, please contact us at:
        </Typography>
        <Typography className="fs-14 my-10">
          Email: <a href="mailto:contact@siki.io">contact@siki.io</a>
        </Typography>
        <Typography className="fs-14 my-10">
          Address: {companyAddress}
        </Typography>
      </div>
    </Box>
  );
}
